import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import type { ApolloCache } from '@apollo/client'

import FormattedMessage from 'components/FormattedMessage'
import Icon from 'components/Icon'
import Button from 'components/Button'

import {
  CollectionsDocument,
  CollectionsQuery,
  useAddCollectionMutation,
  useRemoveCollectionMutation,
} from 'hooks/collections/graphql.generated'

import { Collection } from 'generated/types'
import { RootState } from 'store/reducer'

import styles from './index.module.scss'
import classNames from 'helpers/css'

interface Props {
  providerId: number | string | null | undefined
  collections: Collection[]
  className?: string
  showLabel?: boolean
}

const updateCollectionCache = (
  cache: ApolloCache<unknown>,
  providerId: string,
  selected: boolean,
) => {
  const cached = cache.readQuery<CollectionsQuery>({ query: CollectionsDocument })

  if (!cached) return

  const collections = cached.collections.filter(collection => collection.id !== providerId)

  if (selected) {
    collections.push({ __typename: 'Collection', id: providerId })
  }

  cache.writeQuery({
    query: CollectionsDocument,
    data: { collections },
  })
}

interface ControlProps {
  providerId: number | string
  collections: Collection[]
  className?: string
  showLabel: boolean
}

// The Apollo mutation hooks live here, in a child that is only mounted once the
// button is actually going to render (signed in + hydrated). This keeps trees
// that render <CollectionButton> for a logged-out user free of any Apollo
// dependency — the hooks never run, so no ApolloProvider is required.
const CollectionButtonControl: React.FC<ControlProps> = ({
  providerId,
  collections,
  className,
  showLabel,
}) => {
  const [addCollection, { loading: adding }] = useAddCollectionMutation()
  const [removeCollection, { loading: removing }] = useRemoveCollectionMutation()
  const isUpdating = adding || removing

  const saved = collections.some(collection => collection.id === String(providerId))
  const iconClassName = classNames(
    styles,
    'collection-button__icon',
    saved && 'collection-button__icon--saved',
  )

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const id = String(providerId)
    const isSaved = collections.some(collection => collection.id === id)

    if (isSaved) {
      removeCollection({
        variables: { providerId: id },
        awaitRefetchQueries: true,
        refetchQueries: ['Collections'],
        optimisticResponse: {
          removeCollection: { __typename: 'RemovePayload', id },
        },
        update: cache => updateCollectionCache(cache, id, false),
      })
    } else {
      addCollection({
        variables: { providerId: id },
        awaitRefetchQueries: true,
        refetchQueries: ['Collections'],
        optimisticResponse: {
          addCollection: { __typename: 'AddPayload', id },
        },
        update: cache => updateCollectionCache(cache, id, true),
      })
    }
  }, [providerId, collections, addCollection, removeCollection])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      handleClick(event as unknown as React.MouseEvent)
    }
  }, [handleClick])

  if (showLabel) {
    return (
      <Button
        type="action-bordered"
        size="large"
        htmlType="button"
        className="ant-btn--with-border-radius-small"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isUpdating}
        aria-pressed={saved}
        aria-label={saved ? 'Remove from collection' : 'Add to collection'}
      >
        <Icon
          name={saved ? 'heart' : 'heart-o'}
          className={iconClassName}
          aria-hidden="true"
        />
        <span className={classNames(styles, 'collection-button__label')}>
          {saved ? (
            <FormattedMessage id="collectionButton.saved" defaultMessage="In Ihrer Auswahl" />
          ) : (
            <FormattedMessage id="collectionButton.save" defaultMessage="Zur Auswahl hinzufügen" />
          )}
        </span>
      </Button>
    )
  }

  return (
    <button
      type="button"
      className={classNames(styles, 'collection-button', className, {
        [classNames(styles, 'collection-button--saved')]: saved,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={isUpdating}
      aria-pressed={saved}
      aria-label={saved ? 'Remove from collection' : 'Add to collection'}
    >
      <Icon
        name={saved ? 'heart' : 'heart-o'}
        className={iconClassName}
        aria-hidden="true"
      />
    </button>
  )
}

const CollectionButton: React.FC<Props> = ({
  providerId,
  collections,
  className,
  showLabel = false,
}) => {
  const { isSignedIn, collectionsEnabled } = useSelector((state: RootState) => ({
    isSignedIn: state.currentUser.isSignedIn,
    collectionsEnabled: state.currentUser.collectionsEnabled,
  }))

  if (providerId == null || !isSignedIn || !collectionsEnabled) {
    return null
  }

  return (
    <CollectionButtonControl
      providerId={providerId}
      collections={collections}
      className={className}
      showLabel={showLabel}
    />
  )
}

export default CollectionButton
